import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PinInput from '../components/PinInput';
import ShareBar from '../components/ShareBar';
import MessageList from '../components/MessageList';
import InputBar from '../components/InputBar';
import LanguageSwitch from '../components/LanguageSwitch';
import { ArrowLeftIcon, ExclamationCircleIcon } from '../components/icons';
import { useClipStore } from '../stores/clipStore';
import { useWebSocket } from '../hooks/useWebSocket';
import { useI18n } from '../i18n';

const API_URL = import.meta.env.VITE_API_URL || '';

export default function ClipPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { t, locale } = useI18n();

  const [loading, setLoading] = useState(true);
  const [clipExists, setClipExists] = useState(false);
  const [salt, setSalt] = useState<string | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);

  const store = useClipStore();
  const { connect, disconnect } = useWebSocket();

  // Check if we have credentials stored (in memory or localStorage)
  const hasMemoryCredentials = store.uuid === uuid && store.cryptoKey !== null;
  const [hasStoredCredentials, setHasStoredCredentials] = useState(hasMemoryCredentials);
  const [autoJoinAttempted, setAutoJoinAttempted] = useState(false);

  // Fetch clip status
  useEffect(() => {
    if (!uuid) return;

    const checkClip = async () => {
      try {
        const response = await fetch(`${API_URL}/api/clips/${uuid}`);
        const data = await response.json();

        if (response.ok && data.exists) {
          setClipExists(true);
          setSalt(data.salt);
        } else {
          setClipExists(false);
        }
      } catch (e) {
        setClipExists(false);
      } finally {
        setLoading(false);
      }
    };

    checkClip();
  }, [uuid]);

  // Try auto-join from localStorage when clip exists and we haven't tried yet
  useEffect(() => {
    if (!uuid || !salt || autoJoinAttempted || !clipExists) return;
    if (hasMemoryCredentials) return; // Already have credentials in memory

    const attemptAutoJoin = async () => {
      setAutoJoinAttempted(true);
      const result = await store.tryAutoJoin(uuid);

      if (result.success && result.pin && result.salt) {
        // Auto-join with stored credentials
        const joined = await store.joinClip(uuid, result.pin, result.salt, result.isCreator);
        if (joined) {
          setHasStoredCredentials(true);
        }
      }
    };

    attemptAutoJoin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, salt, clipExists, autoJoinAttempted, hasMemoryCredentials]);

  // Connect if we have stored credentials
  useEffect(() => {
    if (hasStoredCredentials && clipExists) {
      connect();
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasStoredCredentials, clipExists]);

  // Handle PIN completion
  const handlePinComplete = async (pin: string) => {
    if (!uuid || !salt) return;

    setPinError(null);

    const success = await store.joinClip(uuid, pin, salt);

    if (success) {
      setHasStoredCredentials(true);
      connect();
    } else {
      setPinError(locale === 'zh' ? '加入失败，请检查 PIN 码' : 'Failed to join. Please check your PIN.');
    }
  };

  const handleBack = () => {
    navigate(locale === 'en' ? '/en' : '/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-500">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          {t.clip.loading}
        </div>
      </div>
    );
  }

  if (!clipExists) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <ExclamationCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t.clip.notFound}</h2>
          <p className="text-gray-600 mb-6">{t.clip.notFoundDesc}</p>
          <button
            onClick={handleBack}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t.clip.backToHome}
          </button>
        </div>
      </div>
    );
  }

  // Show PIN input if not yet authenticated (and not still trying auto-join)
  if (!hasStoredCredentials && autoJoinAttempted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <button
            onClick={handleBack}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-6 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            {locale === 'zh' ? '返回' : 'Back'}
          </button>

          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t.clip.enterPin}</h2>
            <p className="text-gray-600 mb-6">
              {t.clip.pinHint}
            </p>

            <PinInput
              length={6}
              onComplete={handlePinComplete}
              error={pinError || store.error}
              disabled={store.connecting}
            />

            <p className="mt-6 text-sm text-gray-500">
              {t.clip.lockoutWarning}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main chat interface
  const clipUrl = `${window.location.origin}${locale === 'en' ? '/en' : ''}/c/${uuid}`;

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={locale === 'zh' ? '离开剪贴板' : 'Leave clip'}
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="font-semibold text-gray-900">{t.clip.title}</h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Language Switch */}
            <LanguageSwitch />

            {/* Connection Status */}
            <div className="flex items-center gap-2">
              <span
                className={`w-2 h-2 rounded-full ${
                  store.connected ? 'bg-green-500' : 'bg-yellow-500'
                }`}
              />
              <span className="text-sm text-gray-600">
                {store.connected ? t.clip.connected : t.clip.connecting}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Share Bar */}
      {store.pin && <ShareBar url={clipUrl} pin={store.pin} />}

      {/* Messages */}
      <MessageList />

      {/* Input */}
      <InputBar />
    </div>
  );
}
